'use client';

import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,  } from 'recharts';
import { buildGrowthCurveData, BREED_TARGETS, COBB500_TARGETS } from './growthData';

interface Props {
  flockId: string;
  breed: string;
  currentAgeDays: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-800 mb-2">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex items-center gap-2 mb-1">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
          <span className="text-gray-600">{p.name}:</span>
          <span className="font-medium text-gray-800">{p.value != null ? `${p.value.toFixed(3)} kg` : '—'}</span>
        </div>
      ))}
    </div>
  );
}

export default function GrowthCurveChart({ flockId, breed, currentAgeDays }: Props) {
  const targets = BREED_TARGETS[breed] ?? COBB500_TARGETS;

  const chartData = useMemo(
    () => buildGrowthCurveData(flockId, targets),
    [flockId, breed]
  );

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Actual vs Target Growth Curve</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Weight (kg) over age (days) — {breed} standard
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-500 flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-blue-500 inline-block rounded" />
            <span>Target</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-emerald-500 inline-block rounded" />
            <span>Actual</span>
          </div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartData} margin={{ top: 4, right: 16, left: -16, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis
            dataKey="ageLabel"
            tick={{ fontSize: 10, fill: '#9ca3af' }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#9ca3af' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => `${v}kg`}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine
            x={`Day ${currentAgeDays}`}
            stroke="#f59e0b"
            strokeDasharray="4 3"
            label={{ value: 'Today', position: 'top', fontSize: 10, fill: '#f59e0b' }}
          />
          <Line
            type="monotone"
            dataKey="target"
            name="Target Weight"
            stroke="#3b82f6"
            strokeWidth={2}
            strokeDasharray="5 3"
            dot={false}
            connectNulls
          />
          <Line
            type="monotone"
            dataKey="actual"
            name="Actual Weight"
            stroke="#10b981"
            strokeWidth={2.5}
            dot={{ r: 4, fill: '#10b981', strokeWidth: 0 }}
            activeDot={{ r: 6 }}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
